/**
 * Cocon Requests — screen 09, ported from the validated mockup.
 *
 * Four tiles from Ubi's spreadsheet, every figure read live from board 1982041654.
 *
 * ⚠ **Tile 4 shows SIX status columns where the spreadsheet named four.** `On hold` (183
 * tickets, 7% of the board) and `Quality issue` (4) appear in none of the four, so the tile
 * would have counted 187 open tickets nowhere while claiming to show the open backlog — and
 * it would have disagreed with tile 2 by that much on the same screen. Both columns are in,
 * and the flag above the table says so. Question 1 in the spec.
 *
 * ⚠ **A group the spreadsheet never listed is appended, not dropped.** `ODF survey pending`
 * holds 18 real tickets. Matching a list by discarding tickets is the wrong way round.
 *
 * ⚠ **Nothing here is written back to Monday**, and the client has no way to: see
 * `assertReadOnly` in `monday/client.ts`. The board is somebody's live working system.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  OPEN_STATUS_COLUMNS,
  byGroupOrder,
  defaultSortDirection,
  filterTickets,
  formatNumber,
  groupPopRows,
  isUnlistedGroup,
  sortGroupPopRows,
  splitByCompany,
  summariseRequests,
  tallyBy,
  type GroupPopRow,
  type CompanySplit,
  type LabelColours,
  type MondayCompany,
  type RequestTicket,
} from "@cocon-stats/shared";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Inbox,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

/**
 * Short column headings for tile 4.
 *
 * ⚠ The board's exact label is in the `title` of every heading, and every status pill
 * elsewhere on this screen still shows it verbatim. Six full labels plus Group, POP and
 * Total overflow even a wide screen, and the column that fell off the right was Total —
 * the one a reader needs to check a row against the totals above it.
 */
const SHORT_STATUS: Record<string, string> = {
  "Pending action by Partner": "Partner",
  "Pending UF review": "UF review",
  "Working on it": "Working",
  "with IT helpdesk": "IT helpdesk",
  "On hold": "On hold",
  "Quality issue": "Quality",
};
import { toast } from "sonner";

import { SortableHead, useTableView, type SortColumn } from "@/components/SortableTable";
import { Donut } from "@/components/charts/Donut";
import { MondayPill, mondayColour } from "@/components/MondayPill";
import { Picker } from "@/components/Picker";
import { RequestWeekly, TicketsOverTime, useRequestHistory } from "@/components/RequestHistory";
import { DEFAULT_PERIOD, type Period } from "@/components/period";
import { ApiError, apiFetch } from "@/lib/api-client";

interface Meta {
  boardId: string;
  boardName: string;
  fetchedAt: string;
  durationMs: number;
  internal: boolean;
  scopeLabels: string[] | null;
  boardTotal: number | null;
}

interface RequestsResponse {
  meta: Meta;
  colours: LabelColours;
  tickets: RequestTicket[];
  /** Created on the admin screen. A contractor receives only their own. */
  companies: MondayCompany[];
}

export function MondayRequests() {
  const [data, setData] = useState<RequestsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [company, setCompany] = useState<string | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [pops, setPops] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [openAcc, setOpenAcc] = useState<Record<string, boolean>>({});
  /** A POP filter you can type into, separate from the multi-select. */
  const [popQuery, setPopQuery] = useState("");

  const load = useCallback(async (force = false) => {
    try {
      setError(null);
      const result = await apiFetch<RequestsResponse>(
        `/reports/monday-cocon-requests${force ? "?refresh=1" : ""}`,
      );
      setData(result);
    } catch (err) {
      const api = err instanceof ApiError ? err : null;
      setError({
        message: api?.message ?? "The report could not be loaded.",
        // ⚠ 503 means Monday was busy or slow, not that the board is empty. Saying so is the
        // difference between "try again" and somebody believing their backlog vanished.
        retryable: api?.status === 503,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tickets = data?.tickets ?? [];
  const colours = data?.colours ?? {};

  // The week-by-week history: this morning's figures, one period for the trend and the tiles.
  const history = useRequestHistory();
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);

  /** ⚠ ONE filtered set, feeding every tile and every KPI. Two tiles cannot disagree. */
  const shown = useMemo(
    () =>
      filterTickets(tickets, { company: company ?? undefined, groups, pops, search }).filter((t) =>
        // ⚠ The typed POP box narrows on the POP ALONE, unlike the general search which also
        // matches a ticket name or a group. Asked for by name, so it does what its label
        // says and nothing else — a box called "POP" that also matched ticket titles would
        // return rows whose POP does not contain what was typed.
        popQuery.trim() ? (t.pop ?? "").toLowerCase().includes(popQuery.trim().toLowerCase()) : true,
      ),
    [tickets, company, groups, pops, search, popQuery],
  );

  const summary = useMemo(() => summariseRequests(shown), [shown]);
  const statusSplit = useMemo(() => splitByCompany(shown, (t) => t.status), [shown]);
  const groupSplit = useMemo(
    () => splitByCompany(shown, (t) => t.group).sort((a, b) => byGroupOrder(a.label, b.label)),
    [shown],
  );
  const adminCompanies = data?.companies;
  const tile4Rows = useMemo(() => groupPopRows(shown, adminCompanies ?? []), [shown, adminCompanies]);

  /**
   * The Company filter's list. Ubi: *"list all companies created in the admin menu"* — so
   * those first, every one of them, including a company with no rows today.
   *
   * ⚠ Then, separately labelled, the names on the board that no company claims yet. Leaving
   * them out would make a Jacops row visible in the column but impossible to pick in the
   * filter beneath it, with nothing to say why.
   */
  const companyChoices = useMemo(() => {
    const created = (adminCompanies ?? []).map((c) => c.name);
    const createdSet = new Set(created);
    const notSetUp = [
      ...new Set(
        groupPopRows(tickets, adminCompanies ?? [])
          .map((r) => r.company)
          .filter((c): c is string => c !== null && !createdSet.has(c)),
      ),
    ].sort((a, b) => a.localeCompare(b));
    return [
      { label: "Companies", values: created },
      { label: "On the board, not set up as a company", values: notSetUp },
    ];
  }, [tickets, adminCompanies]);

  /**
   * Tile 4's columns.
   *
   * ⚠ Group and POP carry a filter box and Company a pick-list; the six counts carry
   * nothing. You find a name and you click a count to rank it — a "filter partner" box would
   * be asking for a number, which is what sorting the column already answers.
   */
  const tile4Columns = useMemo<Array<SortColumn<GroupPopRow>>>(
    () => [
      { key: "group", label: "Group", width: 150, value: (r) => r.group, filter: (r) => r.group },
      { key: "pop", label: "POP", width: 130, value: (r) => r.pop, filter: (r) => r.pop },
      {
        key: "company",
        label: "Company",
        width: 130,
        value: (r) => r.company,
        filter: (r) => r.company,
        filterChoices: companyChoices,
      },
      ...OPEN_STATUS_COLUMNS.map((status) => ({
        key: status,
        label: SHORT_STATUS[status] ?? status,
        title: status,
        align: "right" as const,
        value: (r: GroupPopRow) => r.byStatus[status] ?? 0,
      })),
      { key: "total", label: "Total", align: "right" as const, value: (r: GroupPopRow) => r.total },
    ],
    [companyChoices],
  );

  /**
   * ⚠ Tile 4 keeps its OWN comparator. Its default order is not alphabetical — it is Ubi's
   * group order, which is data (her working order, not a sort function) — and
   * `sortGroupPopRows` reproduces it exactly for `group` ascending, so clicking back to the
   * default and never having clicked are the same table.
   */
  const tile4 = useTableView(tile4Rows, tile4Columns, {
    initialSort: { key: "group", direction: "asc" },
    sortRows: (rows, key, direction) => sortGroupPopRows(rows as GroupPopRow[], key, direction),
  });

  // The facets come from the WHOLE scoped board, not from the filtered set — otherwise
  // choosing one company empties the company chips and there is no way back.
  const companies = useMemo(() => tallyBy(tickets, (t) => t.company, "No company"), [tickets]);
  const allGroups = useMemo(
    () => [...new Set(tickets.map((t) => t.group))].sort(byGroupOrder),
    [tickets],
  );
  const allPops = useMemo(
    () => [...new Set(tickets.map((t) => t.pop).filter((p): p is string => p !== null))].sort(),
    [tickets],
  );

  if (loading) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="card flex items-center gap-3 p-6">
          <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Reading the board from Monday…</p>
            {/* ⚠ Says how long and why. A full read of this board is six round trips and takes
                about fifteen seconds; a bare "Loading…" for that long reads as a hang, and
                the next thing somebody does is reload, which starts it again. */}
            <p className="mt-0.5 text-xs text-slate-500">
              About 2 500 tickets — this takes a few seconds the first time. Everyone who opens it in the next few minutes gets
              the same read, instantly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="card p-6" style={{ borderLeft: "4px solid #dc2626" }}>
          <h3 className="font-semibold text-slate-800">Monday could not be read</h3>
          <p className="mt-2 max-w-xl text-sm text-slate-600">{error?.message}</p>
          {/* ⚠ Never "no tickets". An empty screen and a failed read look identical, and
              only one of them means the backlog is clear. */}
          <p className="mt-2 max-w-xl text-xs text-slate-500">
            This is a problem reading the board, not an empty board. Nothing has been changed on Monday.
          </p>
          <button
            type="button"
            className="btn btn-sm gradient-btn mt-4"
            onClick={() => {
              setLoading(true);
              void load(true);
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      </div>
    );
  }

  const meta = data.meta;
  const done = summary.closed;
  const partner = summary.byStatus.find((s) => s.label === "Pending action by Partner")?.n ?? 0;
  const uncovered = shown.filter(
    (t) => t.status !== "Done" && !(OPEN_STATUS_COLUMNS as readonly string[]).includes(t.status),
  ).length;
  const filtered =
    company !== null || groups.length > 0 || pops.length > 0 || search.trim() !== "" || popQuery.trim() !== "";

  return (
    <div className="max-w-[1600px] space-y-6 px-4 py-6 lg:px-8">
      <BoardHeader
        meta={meta}
        refreshing={refreshing}
        onRefresh={() => {
          setRefreshing(true);
          void apiFetch("/reports/monday/refresh", { method: "POST" }).catch(() => undefined);
          void load(true).then(() => toast.success("Board re-read from Monday."));
        }}
      />

      {/* Company filter, shared by every tile */}
      <section className="card flex flex-wrap items-center gap-2 p-4">
        <span className="eyebrow mr-1">Company</span>
        <button
          type="button"
          className={`chip${company === null ? " chip-active" : ""}`}
          onClick={() => setCompany(null)}
        >
          All
        </button>
        {companies.map((c) => (
          <button
            key={c.label}
            type="button"
            className={`chip${company === c.label ? " chip-active" : ""}`}
            onClick={() => setCompany(company === c.label ? null : c.label)}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: mondayColour(colours, "company", c.label) }}
            />
            {c.label} <span className="num text-slate-400">{formatNumber(c.n)}</span>
          </button>
        ))}
        {/* ⚠ Tickets with no company at all. They are in the totals and in nobody's column,
            so the chip exists rather than leaving them unreachable. */}
        {summary.withoutCompany > 0 && company === null && (
          <span className="ml-auto text-xs" style={{ color: "#b45309" }}>
            {formatNumber(summary.withoutCompany)} with no company set
          </span>
        )}

        {/*
          ⚠ Group and POP used to live inside tile 3's card, where they changed tile 4's
          numbers from two tiles away. Filters that silently drive a table you cannot see
          from them are filters nobody finds — reported from the real screen. They are here,
          in the one place filters live, and the line below says they apply to everything.
        */}
        <div className="flex w-full flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <span className="eyebrow mr-1">Filter</span>
          <Picker label="Group" options={allGroups} selected={groups} onChange={setGroups} flag={isUnlistedGroup} />
          <Picker label="POP" options={allPops} selected={pops} onChange={setPops} />

          {/* A POP box you can type into, asked for by name. It narrows on the POP alone. */}
          <div className="relative w-full sm:w-52">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-3.5 w-3.5" />
            </span>
            <input
              type="text"
              value={popQuery}
              onChange={(e) => setPopQuery(e.target.value)}
              placeholder="Search POP"
              aria-label="Search POP"
              className="field field-sm field-icon-sm mono"
            />
            {popQuery && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setPopQuery("")}
                aria-label="Clear the POP search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* The general one, which also matches a ticket name or a group. */}
          <div className="relative w-full sm:w-60">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-3.5 w-3.5" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ticket, group or POP"
              aria-label="Search tickets"
              className="field field-sm field-icon-sm"
            />
            {search && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setSearch("")}
                aria-label="Clear the search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {filtered && (
            <button
              type="button"
              className="text-xs font-semibold"
              style={{ color: "#ed125f" }}
              onClick={() => {
                setCompany(null);
                setGroups([]);
                setPops([]);
                setSearch("");
                setPopQuery("");
              }}
            >
              Clear all
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">
            {filtered ? (
              <>
                Every tile shows{" "}
                <span className="num font-semibold text-slate-600">{formatNumber(summary.total)}</span> of{" "}
                {formatNumber(tickets.length)} tickets
              </>
            ) : (
              "Filters apply to tiles 1–4; the weekly figures follow the Company only"
            )}
          </span>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Kpi accent="rose" label="Tickets" value={summary.total} note={filtered ? "Matching the filters" : "On the board"} />
        <Kpi accent="orange" label="Open" value={summary.open} note="Status is not Done" />
        <Kpi
          accent="emerald"
          label="Done"
          value={done}
          note={`${summary.total ? Math.round((done / summary.total) * 100) : 0}% of these tickets`}
          tone="#047857"
        />
        <Kpi accent="red" label="Waiting on a partner" value={partner} note="The largest single status" tone="#b91c1c" />
      </section>

      {/* Tiles 1 and 3 — pies with per-company accordions */}
      <section className="grid items-start gap-4 xl:grid-cols-2">
        <TileCard title="Tickets Per Status" tile="Tile 1">
          <Donut
            centreLabel="Tickets"
            slices={summary.byStatus.map((s) => ({
              label: s.label,
              n: s.n,
              color: mondayColour(colours, "status", s.label),
            }))}
          />
          <Accordion
            id="status"
            label="Split per company"
            open={openAcc.status ?? false}
            onToggle={() => setOpenAcc((a) => ({ ...a, status: !a.status }))}
            rows={statusSplit}
            colours={colours}
            colourGroup="status"
          />
        </TileCard>

        <TileCard title="Tickets Per Group" tile="Tile 3">
          {groupSplit.length === 0 ? (
            <Empty />
          ) : (
            <Donut
              centreLabel="Tickets"
              slices={groupSplit.map((g) => ({
                label: g.label,
                n: g.total,
                // The GROUP's own colour on the board, never the `Type` column's. Ubi: *"Group
                // colours should match the board"* — borrowing Type's palette painted Service
                // request blue where the board has it brown, and Cocon errors grey.
                color: mondayColour(colours, "group", g.label),
              }))}
            />
          )}
          <Accordion
            id="group"
            label="Split per company"
            open={openAcc.group ?? false}
            onToggle={() => setOpenAcc((a) => ({ ...a, group: !a.group }))}
            rows={groupSplit}
            colours={colours}
            colourGroup="group"
          />
          {allGroups.some(isUnlistedGroup) && (
            <p className="mt-4 text-xs" style={{ color: "#b45309" }}>
              ⚠ {allGroups.filter(isUnlistedGroup).join(", ")} {allGroups.filter(isUnlistedGroup).length === 1 ? "is" : "are"}{" "}
              on the board but not in the spreadsheet&rsquo;s list of groups. Shown last rather than dropped.
            </p>
          )}
        </TileCard>
      </section>

      {/* The same tickets per status, partner or group — at every week end since the board began. Folded. */}
      <TicketsOverTime state={history} company={company} colours={colours} period={period} onPeriod={setPeriod} />

      {/* Tile 2 — open vs closed on current status */}
      <section className="card overflow-hidden p-0">
        <TileHeader title="Open And Closed Tickets" tile="Tile 2" />
        <div className="space-y-5 p-5">
          <div>
            <div className="mb-2 flex flex-wrap items-end justify-between gap-4">
              <div className="flex items-baseline gap-6">
                <div>
                  <p className="eyebrow">Open</p>
                  <p className="num mt-0.5 text-3xl font-bold text-slate-900">
                    {formatNumber(summary.open)}
                    <span className="ml-1 text-sm font-semibold text-slate-400">
                      {summary.total ? Math.round((summary.open / summary.total) * 100) : 0}%
                    </span>
                  </p>
                </div>
                <div>
                  <p className="eyebrow">Closed</p>
                  <p className="num mt-0.5 text-3xl font-bold" style={{ color: "#047857" }}>
                    {formatNumber(done)}
                    <span className="ml-1 text-sm font-semibold text-slate-400">
                      {summary.total ? Math.round((done / summary.total) * 100) : 0}%
                    </span>
                  </p>
                </div>
              </div>
              {/* ⚠ Says what closed means, because "On hold" being open surprises people. */}
              <p className="text-xs text-slate-400">Closed means Status is Done. Every other status is open.</p>
            </div>
            <div className="flex h-6 overflow-hidden rounded-lg bg-slate-100">
              <div
                className="flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: "#ed125f", width: `${summary.total ? (summary.open / summary.total) * 100 : 0}%` }}
              >
                {summary.open > 0 && formatNumber(summary.open)}
              </div>
              <div
                className="flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: "#00c875", width: `${summary.total ? (done / summary.total) * 100 : 0}%` }}
              >
                {done > 0 && formatNumber(done)}
              </div>
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">Per company</p>
            <div className="space-y-2">
              {companies.map((c) => {
                const forCompany = shown.filter((t) => (t.company ?? "No company") === c.label);
                const openN = forCompany.filter((t) => t.status !== "Done").length;
                const doneN = forCompany.length - openN;
                const width = (n: number) => `${forCompany.length ? (n / forCompany.length) * 100 : 0}%`;
                return (
                  <div key={c.label} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 truncate text-xs text-slate-500">{c.label}</span>
                    <div className="flex h-5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div style={{ background: "#ed125f", width: width(openN) }} title={`${openN} open`} />
                      <div style={{ background: "#00c875", width: width(doneN) }} title={`${doneN} done`} />
                    </div>
                    <span className="num w-24 shrink-0 text-right text-xs text-slate-500">
                      <span className="font-bold text-slate-800">{formatNumber(openN)}</span> / {formatNumber(forCompany.length)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Tile 4 — open tickets per group × POP */}
      <section className="card overflow-hidden">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Open Tickets Per Group And POP</span>
          <span className="flex items-center gap-3 text-xs text-white/85">
            {formatNumber(tile4.rows.length)} rows · {formatNumber(summary.open)} open tickets
            {tile4.isFiltered && (
              <button
                type="button"
                className="rounded-full px-2 py-0.5 font-semibold"
                style={{ background: "rgba(255,255,255,.2)" }}
                onClick={tile4.clearFilters}
                title={`${tile4.total - tile4.rows.length} rows hidden by the column filters`}
              >
                Clear column filters
              </button>
            )}
          </span>
        </div>

        <div className="flex items-start gap-2.5 px-5 py-3" style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b45309" }} />
          <p className="text-xs" style={{ color: "#b45309" }}>
            The spreadsheet asked for four status columns; the board has <span className="font-bold">six</span> non-Done
            statuses. <span className="font-bold">On hold and Quality issue are included here</span> — without them
            these rows would not add up to the {formatNumber(summary.open)} open tickets above. Question 1 in the spec.
            {uncovered > 0 && (
              <> ⚠ {formatNumber(uncovered)} open tickets carry a status not in any column — the board has gained one.</>
            )}
          </p>
        </div>

        {/* ⚠ Both axes named. `overflow-x-auto` alone leaves overflow-y to the
            blockification rule, so whether this div is the scroll container — and therefore
            whether the sticky filter row sticks to it — depended on a subtlety rather than
            on anything written down. */}
        <div className="custom-scrollbar overflow-x-auto overflow-y-auto" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <SortableHead columns={tile4Columns} view={tile4} sticky />
            <tbody>
              {tile4.rows.map((row) => (
                <tr key={`${row.group}-${row.pop ?? "none"}-${row.company ?? "none"}`}>
                  {/* ⚠ `wrap` on the two name columns. The Company column pushed the table
                      78 px past its card at 1440 px, hiding Total off the right edge, and
                      what held it wide was "ODF survey pending Extra" on one line — measured,
                      29 Sep 2026. The counts stay on one line; a name may take two. */}
                  <td className="wrap text-slate-700">
                    {row.group}
                    {isUnlistedGroup(row.group) && (
                      <span className="pill pill-amber ml-2" title="On the board, not in the spreadsheet">
                        Extra
                      </span>
                    )}
                  </td>
                  <td className="mono text-slate-600">
                    {/* ⚠ 14 open tickets really have no POP. Surfaced as a row of its own —
                        the same treatment as the 20 DMP orders with no contractor. */}
                    {row.pop ?? <span className="pill pill-slate">No POP</span>}
                  </td>
                  <td className="wrap text-slate-600">
                    {row.company ?? <span className="pill pill-slate">No company</span>}
                  </td>
                  {OPEN_STATUS_COLUMNS.map((status) => {
                    const n = row.byStatus[status] ?? 0;
                    return (
                      <td key={status} className="num text-right">
                        {n === 0 ? <span className="text-slate-200">·</span> : <span className="text-slate-700">{n}</span>}
                      </td>
                    );
                  })}
                  <td className="num text-right font-bold text-slate-800">{row.total}</td>
                </tr>
              ))}
              {tile4.rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-14 text-center">
                    <Inbox className="mx-auto h-6 w-6 text-slate-300" />
                    <p className="mt-3 text-sm text-slate-500">
                      {tile4.isFiltered
                        ? "No row matches the column filters."
                        : "No open tickets match these filters."}
                    </p>
                    {tile4.isFiltered && (
                      <button
                        type="button"
                        className="mt-1 text-sm font-semibold"
                        style={{ color: "#ed125f" }}
                        onClick={tile4.clearFilters}
                      >
                        Clear the column filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
          <span className="num font-semibold text-slate-700">
            {formatNumber(tile4.rows.reduce((n, r) => n + r.total, 0))}
          </span>{" "}
          open tickets across {formatNumber(tile4.rows.length)} group, POP and company combinations
          {/* ⚠ Never silent about a filter. A shortened table read as the whole story is
              exactly the failure this product exists to prevent. */}
          {tile4.isFiltered && (
            <>
              {" "}
              · <span style={{ color: "#b45309" }}>{formatNumber(tile4.total - tile4.rows.length)} rows hidden by the column filters</span>
            </>
          )}
          {summary.openWithoutPop > 0 && (
            <>
              {" "}
              · <span className="num font-semibold">{formatNumber(summary.openWithoutPop)}</span> of them have no POP set
              on the board
            </>
          )}
        </div>
      </section>

      {/* Pending per partner, closed tickets, how tickets move — the weekly meetings run on these */}
      <h2 className="pt-2 text-lg font-bold text-slate-800">How Tickets Are Processed, Week By Week</h2>
      <RequestWeekly state={history} company={company} colours={colours} period={period} onPeriod={setPeriod} />

      <p className="text-xs text-slate-400">
        Source: Monday.com board <span className="mono">{meta.boardId}</span>, read-only — nothing is ever written
        back. Group and status names are the board&rsquo;s own; only the column headings above are shortened, and each
        one carries the full label.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function BoardHeader({
  meta,
  refreshing,
  onRefresh,
}: {
  meta: Meta;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const read = new Date(meta.fetchedAt);
  const age = Math.round((Date.now() - read.getTime()) / 60000);

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
      <span className="flex items-center gap-2 text-slate-600">
        <RefreshCw className={`h-4 w-4 text-slate-400${refreshing ? " animate-spin" : ""}`} />
        Read{" "}
        <span className="font-semibold text-slate-800">
          {read.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </span>
        {/* ⚠ The stamp is the read's real time, never the clock. Everyone opening this
            screen inside the cache window sees the identical board, which is the point of
            the product stated for a live source. */}
        <span className="text-slate-400">({age < 1 ? "just now" : `${age} min ago`})</span>
      </span>
      <span className="hidden text-slate-400 sm:inline">·</span>
      <span className="text-slate-500">
        Monday.com board <span className="mono">{meta.boardId}</span> · read-only
      </span>
      {meta.boardTotal !== null && (
        <span className="text-slate-400">· {formatNumber(meta.boardTotal)} items on the board</span>
      )}
      <button type="button" className="btn btn-sm btn-outline ml-auto" disabled={refreshing} onClick={onRefresh}>
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </button>
    </div>
  );
}

export function Kpi({
  accent,
  label,
  value,
  note,
  tone,
}: {
  accent: string;
  label: string;
  value: number;
  note: string;
  tone?: string;
}) {
  return (
    <div className={`glass-card card-accent-${accent} rounded-xl p-5`}>
      <p className="eyebrow">{label}</p>
      <p className="num mt-1 text-3xl font-bold" style={{ color: tone ?? "#0f172a" }}>
        {formatNumber(value)}
      </p>
      <p className="mt-1 text-xs text-slate-500">{note}</p>
    </div>
  );
}

export function TileHeader({ title, tile }: { title: string; tile: string }) {
  return (
    <div className="gradient-band flex items-center justify-between px-5 py-3 text-white">
      <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
      <span className="text-[11px] text-white/80">{tile}</span>
    </div>
  );
}

export function TileCard({
  title,
  tile,
  children,
}: {
  title: string;
  tile: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card overflow-hidden p-0">
      <TileHeader title={title} tile={tile} />
      <div className="p-5">{children}</div>
    </div>
  );
}

function Empty() {
  return (
    <div className="py-10 text-center">
      <Inbox className="mx-auto h-6 w-6 text-slate-300" />
      <p className="mt-3 text-sm text-slate-500">Nothing matches these filters.</p>
    </div>
  );
}

/**
 * The per-company split under a pie.
 *
 * ⚠ Built from the SAME tickets the pie counted, so the accordion cannot disagree with the
 * slice above it — which is the failure this whole product exists to prevent, in miniature.
 */
function Accordion({
  id,
  label,
  open,
  onToggle,
  rows,
  colours,
  colourGroup,
}: {
  id: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  rows: CompanySplit[];
  colours: LabelColours;
  colourGroup: string;
}) {
  const companies = useMemo(() => {
    const names = new Set<string>();
    for (const row of rows) for (const name of Object.keys(row.byCompany)) names.add(name);
    return [...names].sort();
  }, [rows]);

  // ⚠ Sortable like every other table on these screens, even at nine rows: a reader who has
  // learned that a heading sorts should not have to work out which tables opted out.
  const columns = useMemo<Array<SortColumn<CompanySplit>>>(
    () => [
      { key: "label", label: "", width: 150, value: (row) => row.label },
      ...companies.map((c) => ({
        key: c,
        label: c,
        align: "right" as const,
        value: (row: CompanySplit) => row.byCompany[c] ?? 0,
      })),
      { key: "total", label: "Total", align: "right" as const, value: (row: CompanySplit) => row.total },
    ],
    [companies],
  );
  const view = useTableView(rows, columns, { initialSort: { key: "total", direction: "desc" } });

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-ghost mt-4 w-full justify-between"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`acc-${id}`}
      >
        <span>{label}</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && (
        <div id={`acc-${id}`} className="custom-scrollbar mt-2 overflow-x-auto border-t border-slate-100 pt-2">
          <table className="tbl">
            <SortableHead columns={columns} view={view} />
            <tbody>
              {view.rows.map((row) => (
                <tr key={row.label}>
                  <td>
                    <MondayPill colours={colours} group={colourGroup} label={row.label} />
                  </td>
                  {companies.map((c) => (
                    <td key={c} className="num text-right text-slate-600">
                      {row.byCompany[c] ?? <span className="text-slate-200">·</span>}
                    </td>
                  ))}
                  <td className="num text-right font-bold text-slate-800">{formatNumber(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
